import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import TestimonialCard from "../TestimonialCard";
import useEmblaCarousel from "embla-carousel-react";
import { useIsMobile } from "@/hooks/use-mobile";
import SwipeHint from "@/components/ui/SwipeHint";

const TestimonialsSection = () => {
  const { t } = useTranslation('home');
  const [isHovered, setIsHovered] = useState(false);
  const isMobile = useIsMobile();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [headerVisible, setHeaderVisible] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  // Header reveal animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHeaderVisible(true);
        }
      },
      { threshold: 0.5 }
    );
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }
    return () => observer.disconnect();
  }, []);

  // Update selected index when carousel scrolls
  useEffect(() => {
    if (!emblaApi) return;
    
    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };
    
    emblaApi.on("select", onSelect);
    onSelect();
    
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  const testimonials = [
    {
      quote:
        "The service was exceptional. The cost for the repairs very reasonable also the workers was very respectful and courteous. I will definitely use them in the future and highly recommend them.",
      name: "Charlie R.",
      title: "Reasonable price",
      rating: 4.5,
      service: "Roof repair",
      location: "Conroe, TX",
    },
    {
      quote:
        "Jared came out and inspected my roof. I thought I needed a full replacement but he recommended just repairing the damaged sections. Saved me over $10,000. That kind of honesty is hard to find. Ruth in the office was great about scheduling everything around my work schedule. When I do need a full replacement eventually, I know who I'm calling.",
      name: "Francisco G.",
      title: "Honest assessment saved me money",
      rating: 5,
      service: "Roof repair",
      location: "Humble, TX",
    },
    {
      quote:
        "We needed to sell my mom's house and the roof was the main issue holding up the sale. Insurance wouldn't cover it since it was age-related wear. Ben worked with us to find options that fit our budget and timeline. Crew finished in 3 days and the buyer's inspector approved everything. House closed two weeks later. Really appreciate them understanding our situation.",
      name: "Jennifer M.",
      title: "They worked with our budget",
      rating: 5,
      service: "Roof replacement",
      location: "Atascocita, TX",
    },
    {
      quote:
        "Called Altum after noticing a leak during the storms last month. Ruth got us on the schedule quickly and Jose came out same day. He explained everything clearly with photos and what my options were. Insurance ended up denying our claim but Jose provided a detailed out-of-pocket quote that was fair. Crew showed up when they said they would, finished on time, and cleaned up everything. No surprises on the final bill.",
      name: "Robert H.",
      title: "Professional and reliable",
      rating: 5,
      service: "Roof repair",
      location: "Aldine, TX",
    },
    {
      quote:
        "After getting ghosted by another roofing company, we were hesitant to try again. Altum was completely different. Michael walked us through the entire process, answered all our questions without pressure, and kept us updated daily during the installation. The crew was respectful of our property and the quality of work is excellent. Finally have peace of mind knowing our roof is done right.",
      name: "David C.",
      title: "Great communication throughout",
      rating: 5,
      service: "Roof replacement",
      location: "Spring, TX",
    },
  ];

  return (
    <section id="testimonials" className="py-20 bg-secondary">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Altum Contractors",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "5",
              "bestRating": "5",
              "worstRating": "4.5"
            },
            "review": testimonials.map((t) => ({
              "@type": "Review",
              "reviewRating": {
                "@type": "Rating",
                "ratingValue": t.rating.toString(),
                "bestRating": "5"
              },
              "author": {
                "@type": "Person",
                "name": t.name
              },
              "reviewBody": t.quote,
              "datePublished": "2025-11-01"
            }))
          })}
        </script>
      </Helmet>
      <div className="container mx-auto px-4">
        <div
          ref={headerRef}
          className={`text-center mb-16 transition-all duration-1000 ease-out ${
            headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="section-eyebrow !text-primary/80">{t('testimonials.eyebrow')}</span>
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-white mb-4">
            {t('testimonials.headline')}
          </h2>
          <p
            className={`text-lg text-white/80 max-w-2xl mx-auto transition-all duration-1000 ease-out delay-200 ${
              headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            {t('testimonials.subheadline')}
          </p>
        </div>

        {/* Desktop: Auto-scrolling animation that pauses on hover */}
        <div className="hidden md:block relative overflow-hidden max-w-7xl mx-auto">
          <div
            className="flex gap-6 animate-scroll"
            style={{ animationPlayState: isHovered ? 'paused' : 'running' }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {[...testimonials, ...testimonials, ...testimonials, ...testimonials].map((testimonial, index) => (
              <div key={index} className="flex-shrink-0 w-[calc(33.333%-16px)]">
                <TestimonialCard
                  quote={testimonial.quote}
                  name={testimonial.name}
                  title={testimonial.title}
                  rating={testimonial.rating}
                  service={testimonial.service}
                  location={testimonial.location}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Mobile: Swipeable carousel - one card at a time */}
        <div className="md:hidden relative">
          {/* Swipe Hint */}
          <SwipeHint />
          
          {/* Edge gradients to hint more content */}
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-secondary to-transparent z-[5] pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-secondary to-transparent z-[5] pointer-events-none" />
          
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="flex-[0_0_100%] min-w-0 px-4">
                  <TestimonialCard
                    quote={testimonial.quote}
                    name={testimonial.name}
                    title={testimonial.title}
                    rating={testimonial.rating}
                    service={testimonial.service}
                    location={testimonial.location}
                  />
                </div>
              ))}
            </div>
          </div>
          
          {/* Dot indicators */}
          <div className="flex justify-center gap-2 mt-6">
            {testimonials.map((_, index) => (
              <button
                key={index}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === selectedIndex ? 'bg-primary w-8' : 'bg-white/30'
                }`}
                onClick={() => emblaApi?.scrollTo(index)}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
        
        <style>{`
          @keyframes scroll {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-25%);
            }
          }
          
          @keyframes scroll-mobile {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(calc(-100% / 3));
            }
          }
          
          .animate-scroll {
            animation: scroll 20s linear infinite;
          }

          .animate-scroll-mobile {
            animation: scroll-mobile 25s linear infinite;
          }
        `}</style>
      </div>
    </section>
  );
};

export default TestimonialsSection;
